import dayjs from 'dayjs';
import { useState, type JSX } from 'react';
import { DesktopTimePicker as MUITimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { type TextFieldProps } from '@mui/material/TextField';
import { TimeInputField, type TimeInputFieldProps } from './timeInputField';
import { useTheme } from '../../../../theme';
import { IconButton, type IconButtonProps } from '../../iconButton';
import { Icon } from '@doxyme/icons';
import { useIntl, useLocale } from '@doxyme/locale';
import { useTimeInputStyles } from './timeInput.styles';
import { type InputProps } from '../input';
import { getPickersInputLocaleText } from '../../../../utils';

/*
 We have to use Date | null here and not 'undefined' as MUI considers `undefined values to be uncontrolled components.
 If this type changes please check you don't start getting MUI uncontrolled --> controlled component errors.
 */
export type TimeInputValue = Date | null;
export type TimeInputProps = Omit<InputProps, 'onChange' | 'value'> & {
  /**
   * Initial Date value
   */
  value: TimeInputValue;

  /**
   * To get selected time
   */
  onChange?: (value: TimeInputValue) => void;

  disabled?: boolean;

  // The positioning of the MUI digital clock.
  clockPopoverPlacement?: 'bottom-end' | 'bottom-start';

  className?: string;
};

export const TimeInput = ({
  value,
  onChange,
  className,
  disabled,
  hasError,
  clockPopoverPlacement,
  ...inputProps
}: TimeInputProps): JSX.Element => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const styles = useTimeInputStyles();
  const { formatMessage } = useIntl();

  const { locale } = useLocale();

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={locale}
      localeText={getPickersInputLocaleText(locale)}
    >
      <MUITimePicker
        value={value !== null ? dayjs(value) : null}
        className={className}
        css={{
          color: theme.icon.secondary,
        }}
        onChange={(data) => {
          onChange?.(data === null ? null : data.toDate());
        }}
        format='hh:mm A'
        ampm={true}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onOpen={() => {
          setIsOpen(true);
        }}
        sx={{ ...styles.icons, ...styles.input }}
        slots={{
          actionBar: () => null,
          textField: TimeInputField,
          openPickerButton: (props) => (
            <IconButton
              icon={<Icon.Clock type='stroke' />}
              {...props}
              type='subtle'
              size='medium'
              onClick={props.onClick as IconButtonProps['onClick']}
              data-testid='time-input-open-picker-button'
              aria-label={formatMessage({
                id: 'input.timeInput.openPicker.label',
              })}
            />
          ),
          clearButton: (props) => (
            <IconButton
              icon={<Icon.Cross type='filled' size='small' />}
              {...props}
              type='subtle'
              size='medium'
              title={formatMessage({ id: 'textFieldClearTooltip' })}
            />
          ),
        }}
        slotProps={{
          field: { clearable: true },
          digitalClockSectionItem: {
            disableRipple: true,
          },
          popper: {
            sx: { ...styles.popper },
            popperOptions:
              clockPopoverPlacement !== undefined
                ? {
                    placement: clockPopoverPlacement,
                  }
                : undefined,
          },
          layout: {
            sx: { ...styles.digitalClock, ...styles.digitalClockItem },
          },
          toolbar: {
            hidden: true,
          },
          actionBar: {
            hidden: true,
          },
          textField: {
            ...inputProps,
            hasError,
          } as unknown as TimeInputFieldProps as TextFieldProps,
        }}
        disabled={disabled}
      />
    </LocalizationProvider>
  );
};
